import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../../apiConfig';
import Navbar from '../../navbar.jsx';
import Footer from '../../footer.jsx';

const ResultsPage = () => {
  const { slug, attemptId } = useParams(); // Added attemptId
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch attempts
        console.log('Fetching attempts for slug:', slug);
        const attemptsResponse = await axios.get(
          `${API_URL}/api/v1/admin/public/assessments/${slug}/user-attempts`,
          { withCredentials: true }
        );
        console.log('Attempts response:', attemptsResponse.data);
        if (!attemptsResponse.data.success) {
          throw new Error(attemptsResponse.data.message || 'Failed to fetch attempts');
        }

        // Filter attempts by attemptId if provided
        const filteredAttempts = attemptId
          ? attemptsResponse.data.data.filter((attempt) => attempt._id === attemptId)
          : attemptsResponse.data.data;

        // Fetch questions
        let questionsData = [];
        try {
          console.log('Fetching questions for slug:', slug);
          const questionsResponse = await axios.get(
            `${API_URL}/api/v1/admin/public/assessments/${slug}/questions`,
            { withCredentials: true }
          );
          console.log('Questions response:', questionsResponse.data);
          if (Array.isArray(questionsResponse.data.questions)) {
            questionsData = questionsResponse.data.questions;
            setQuestions(questionsData);
          } else {
            throw new Error('Invalid questions response format');
          }
        } catch (questionError) {
          console.error('Questions fetch error:', {
            message: questionError.message,
            response: questionError.response?.data,
            status: questionError.response?.status,
            config: questionError.config,
          });
          setError('Failed to fetch questions. Displaying results without correct answers.');
          toast.error('Failed to fetch questions. Some data may be incomplete.', { autoClose: 2500 });
        }

        // Merge correctAnswers into attempts
        const updatedAttempts = filteredAttempts.map((attempt) => ({
          ...attempt,
          answers: attempt.answers.map((ans) => {
            const question = questionsData.find((q) => q._id === ans.question._id);
            return {
              ...ans,
              question: {
                ...ans.question,
                options: question ? question.options : ans.question.options,
                correctAnswers: question ? question.correctAnswers : [],
                type: question ? question.type : 'single',
              },
            };
          }),
        }));

        setAttempts(updatedAttempts);
      } catch (error) {
        console.error('Fetch error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: error.config,
        });
        setError(error.message || 'Error fetching data');
        toast.error(error.message || 'Error fetching data', { autoClose: 2500 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, attemptId]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-blue-600 text-xl animate-pulse">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && attempts.length === 0) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="bg-red-100 text-red-700 p-6 rounded-xl shadow-lg">
            {error}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Navbar />
      <main className="flex-grow p-6 max-w-7xl mx-auto">
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6">
            {error}
          </div>
        )}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-raleway font-bold text-gray-800">Your Assessment Results</h2>
          <button
            onClick={() => navigate(`/assessments/${slug}`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-full font-montserrat text-sm hover:bg-blue-700 transition"
          >
            Back to Assessment
          </button>
        </div>

        {attempts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <p className="text-gray-600 font-montserrat text-lg">
              {attemptId ? 'Attempt not found.' : 'No attempts found.'}
            </p>
          </div>
        ) : (
          attempts.map((attempt, attemptIndex) => (
            <div
              key={attempt._id}
              className="bg-white rounded-xl shadow-lg p-6 mb-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-raleway font-semibold text-blue-600">
                  Attempt #{attempt.attemptNumber}
                </h3>
                <p className="text-gray-600 font-montserrat text-sm">
                  Submitted: {new Date(attempt.submittedAt).toLocaleString()}
                </p>
              </div>
              <p className="text-lg font-montserrat text-gray-600 mb-4">
                Score: {attempt.score || 'N/A'} / {attempt.answers.length}
              </p>
              <div className="space-y-6">
                {attempt.answers.map((ans, idx) => {
                  const userSelected = ans.selectedOptions || [];
                  const correctAnswers = ans.question.correctAnswers || [];
                  const hasCorrectAnswers = correctAnswers.length > 0;
                  const options = ans.question.options || [];

                  return (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      <p className="text-lg font-montserrat text-gray-700 font-semibold mb-3">
                        Question {idx + 1}: {ans.question.questionText}
                      </p>
                      {!hasCorrectAnswers && (
                        <p className="text-sm font-montserrat text-red-500 mb-2">
                          Correct answers not available for this question.
                        </p>
                      )}
                      {options.length === 0 && (
                        <p className="text-sm font-montserrat text-red-500 mb-2">
                          Options not available for this question.
                        </p>
                      )}
                      <div className="space-y-2">
                        {options.map((option, optIdx) => {
                          const isSelected = userSelected.includes(optIdx);
                          const isCorrect = correctAnswers.includes(optIdx);
                          return (
                            <div
                              key={optIdx}
                              className={`flex items-center space-x-3 p-3 rounded-lg border ${
                                isCorrect && hasCorrectAnswers
                                  ? 'bg-green-100 border-green-300'
                                  : isSelected && hasCorrectAnswers
                                  ? 'bg-red-100 border-red-300'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <input
                                type={ans.question.type === 'single' ? 'radio' : 'checkbox'}
                                checked={isSelected}
                                disabled
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-gray-700 font-montserrat flex-grow">
                                {option}
                              </span>
                              {isCorrect && hasCorrectAnswers && (
                                <i className="fas fa-check text-green-500" aria-label="Correct answer"></i>
                              )}
                              {isSelected && !isCorrect && hasCorrectAnswers && (
                                <i className="fas fa-times text-red-500" aria-label="Incorrect selection"></i>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ResultsPage;